import { useEffect, useRef, useCallback } from "react";
import { Modal, message } from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { v4 as uuidv4 } from "uuid";
import dayjs from "dayjs";
import db from "../../../../../database";
import { getPricingRules } from "../../../../../utils/pricingRulesCache";
import { processSync } from "../../../../Sync/syncData";
import { useWebSocket } from "../../../../../lib/components/WebSocketProvider";
import useSyncProgressStore from "../../../../../stores/useSyncProgressStore";
import useCartStore from "../../../../../stores/useCartStore";
import useSessionStore from "../../../../../stores/useSessionStore";
import useTransactionStore from "../../../../../stores/useTransactionStore";
import useAuthStore from "../../../../../stores/useAuthStore";
import { trace } from "../../../../../utils/debugTrace";

const useSuperMarketEffects = ({
  cart,
  setCart,
  tillData,
  posConfig,
  selectedProductInCart,
  setSelectedProductInCart,
  setStockList,
  cardPaymnetStatusPLB,
  setCardPaymnetStatusPLB,
  isPurchaseReturn,
  searchCustomersRef,
  isParkedBill,
  parkedBillRef,
  themeJSON,
  isLocked,
  setIsOnline,
  getCategoryProducts,
  setProductSearchInput,
  keyboard,
  setKeyValue,
  paymentModal,
  addCashFlag,
  filterdParkedList,
  setCashManagementData,
  handleDeletePayment,
  orderHistoryDetails,
  setOrderHistoryDetails,
  manualDiscountModalVisible,
  manualDiscountForm,
  setManualDiscountInput,
  setSelectedManualDiscountTypeValue,
  handleOutsideClick,
  setSaleTypeData,
  setCurrentDateTime,
  orderDelay,
  setSyncLoad,
  selectedSaleTypeData,
  setTillLink,
  tillDataPaymentMethods,
  setPaymentType,
  handleKey,
  displayReturnOrderSearch,
  retrunSearchType,
  returnBillSearchInputRef,
  debounceTimeoutRef,
  isPendingFetchRef,
  fetchMoreData,
  showPaymentMethods,
  setAmount,
  pricePrecision,
  giftCardFlag,
  isGiftCardFlag,
  loyalityOtpModalVisible,
  isWalletFlag,
  isBankTransferOpen,
  showCardPaymnteModal,
  isCardPaymentFlag,
}) => {
  const { resetProgress } = useSyncProgressStore();

  // Auto-select next item when selected product is deleted from cart.
  // BUG-1926: this only ever checked the return cart (cart.items). During
  // Return & Exchange, a selected EXCHANGE-cart item's productId is never in
  // cart.items (it lives in exchangeCart.items instead), so this effect
  // treated every exchange-item selection as "deleted" and force-reselected
  // the last RETURN line — showing that return line's data (negative weight,
  // isReturn qty-edit guard) in the product-details panel in place of the
  // exchange item the cashier actually clicked.
  useEffect(() => {
    if (!selectedProductInCart || !selectedProductInCart.productId) return;

    const existsInReturnCart = cart.items.some((item) => item.productId === selectedProductInCart.productId);
    const exchangeItems = useCartStore.getState().exchangeCart?.items || [];
    const existsInExchangeCart = exchangeItems.some((item) => item.productId === selectedProductInCart.productId);
    const stillExists = existsInReturnCart || existsInExchangeCart;

    if (!stillExists) {
      trace("SMSelection", "auto-reselect: selected product missing from both carts, reselecting last return item", {
        selectedProductId: selectedProductInCart.productId,
        selectedIsReturn: selectedProductInCart.isReturn,
        selectedWeight: selectedProductInCart.weight,
        returnCartLen: cart.items.length,
        exchangeCartLen: exchangeItems.length,
      });
      if (cart.items.length > 0) {
        setSelectedProductInCart(cart.items[cart.items.length - 1]);
      } else {
        setSelectedProductInCart({});
        setStockList([]);
      }
    }
  }, [cart.items]);

  // Keep the payable "amount" (tendered amount fed into the keypad/handlePayment)
  // synced to cart.total while the plain payment keypad is open. Unlike
  // Core/index.js (useCoreEffects), SuperMarket had no effect re-syncing amount
  // when cart.total changed after the panel opened, so a coupon/discount applied
  // mid-payment left the keypad prefilled with the pre-discount total.
  // Scoped to the plain keypad only (no gift card / loyalty / card / bank-transfer
  // sub-flow open) since those flows share the same `amount` state for their own
  // manual entry and must not have it reset out from under the cashier.
  useEffect(() => {
    if (
      !showPaymentMethods ||
      cart.isReturn ||
      giftCardFlag ||
      isGiftCardFlag ||
      loyalityOtpModalVisible ||
      isWalletFlag ||
      isBankTransferOpen ||
      showCardPaymnteModal ||
      isCardPaymentFlag
    ) {
      return;
    }
    const isExchangeMode = useTransactionStore.getState().selectedReturnType === "EXCHANGE";
    if (isExchangeMode) return;
    const remaining = parseFloat(((Number(cart.total) || 0) - (Number(cart.paid) || 0)).toFixed(pricePrecision));
    setAmount(remaining);
  }, [
    cart,
    showPaymentMethods,
    giftCardFlag,
    isGiftCardFlag,
    loyalityOtpModalVisible,
    isWalletFlag,
    isBankTransferOpen,
    showCardPaymnteModal,
    isCardPaymentFlag,
  ]);

  // Card payment timeout modal
  useEffect(() => {
    if (cardPaymnetStatusPLB) {
      Modal.info({
        icon: null,
        okButtonProps: { style: { display: "none" } },
        okText: "Go Back",
        content: (
          <div
            style={{
              fontSize: "18px",
              fontWeight: "500",
              textAlign: "center",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              paddingLeft: 0,
              fontSize: "1vw",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "58px",
                height: "58px",
                borderRadius: "50%",
                backgroundColor: "rgba(255, 165, 0, 0.2)",
                marginTop: "15px",
              }}
            >
              <ExclamationCircleOutlined
                style={{
                  fontSize: "35px",
                  color: "orange",
                }}
              />
            </div>
            <p style={{ color: "#0F0718", fontWeight: 550, fontSize: "1.8em", padding: "15px 15px 0 15px", marginBottom: 0 }}>Payment Timed Out</p>
            <p style={{ color: "#0F0718", fontWeight: 400, fontSize: "1.25em", padding: "5px 20px 0 20px" }}>
              Your payment request couldn't be processed in time. Please try again
            </p>

            <button
              style={{
                backgroundColor: "#2F3856",
                color: "#fff",
                padding: "10px 50px 10px 50px",
                borderRadius: "10px",
                border: "none",
                cursor: "pointer",
              }}
              onClick={() => {
                setCardPaymnetStatusPLB(false);
                Modal.destroyAll();
              }}
            >
              Try Again
            </button>
          </div>
        ),
        style: {
          textAlign: "center",
          height: "50vh",
          width: "50%",
        },
        modalRender: (modal) => <div style={{ borderRadius: "12px", overflow: "hidden" }}>{modal}</div>,
        bodyStyle: {
          padding: "0",
        },
        closable: false,
        onOk: null,
      });
    }
  }, [cardPaymnetStatusPLB]);

  // Focus search on purchase return
  useEffect(() => {
    let t;
    if (isPurchaseReturn) {
      t = setTimeout(() => {
        searchCustomersRef?.current?.focus();
      }, 10);
    }
    return () => clearTimeout(t);
  }, [isPurchaseReturn]);

  // Focus parked bill input
  useEffect(() => {
    let t;
    if (isParkedBill) {
      t = setTimeout(() => {
        parkedBillRef?.current?.focus();
      }, 10);
    }
    return () => clearTimeout(t);
  }, [isParkedBill]);

  // Theme CSS variable
  useEffect(() => {
    document.documentElement.style.setProperty("--table-bg-color", themeJSON.productTableBGColor || "#fff");
  }, [themeJSON]);

  // Online/offline status
  useEffect(() => {
    const handleOnlineStatus = () => {
      setIsOnline(navigator.onLine);
    };

    window.addEventListener("online", handleOnlineStatus);
    window.addEventListener("offline", handleOnlineStatus);

    return () => {
      window.removeEventListener("online", handleOnlineStatus);
      window.removeEventListener("offline", handleOnlineStatus);
    };
  }, [isLocked]);

  // Initial mount: load cart, focus product search
  useEffect(() => {
    getCategoryProducts();
    const t1 = setTimeout(() => {
      try {
        const cartDetails = useCartStore.getState().cart;
        if (cartDetails !== null) {
          setCart(cartDetails);
        }
      } catch (error) {
        console.error("Error parsing cart details:", error);
      }
    }, 200);

    const t2 = setTimeout(() => {
      setProductSearchInput("");
      const productSearchInput = document.getElementById("sm-product-search");
      if (productSearchInput) {
        productSearchInput.blur();
      }
      keyboard?.current?.setInput("");
    }, 200);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, []);

  // Click outside handler
  useEffect(() => {
    document.addEventListener("click", handleOutsideClick);

    return () => {
      document.removeEventListener("click", handleOutsideClick);
    };
  }, []);

  // Load sale type data
  useEffect(() => {
    const getSaleTypeData = async () => {
      const saleData = await db.posSaletypes.toArray();
      const filteredSaleData = saleData?.filter(
        (sale) => sale?.cwrSaletype?.fulfilmentType !== "TA" && sale?.cwrSaletype?.fulfilmentType !== "DI" && sale?.cwrSaletype?.fulfilmentType !== "DE"
      );

      setSaleTypeData(filteredSaleData);
    };

    getSaleTypeData();
  }, []);

  // Clock update
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentDateTime(new Date());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Auto sync interval
  useEffect(() => {
    let syncOrdersInterval;
    const isSyncingRef = { current: false };

    const startSyncInterval = () => {
      syncOrdersInterval = setInterval(async () => {
        if (isSyncingRef.current) return;
        if (!posConfig || !useCartStore.getState().cart) {
          return;
        }
        // Cart rehydration from storage (App.js) is async and can still be in
        // flight on the first tick(s) after a refresh — reading the cart before
        // it lands sees the default empty state, not the real persisted cart.
        // That false "empty cart" reading was wrongly treated as safe-to-sync,
        // swapping the whole screen for LoadingScreen even when the cart
        // actually had items — every refresh, online or offline.
        if (!useCartStore.persist.hasHydrated()) return;
        const activeElement = document.activeElement;
        const isDrawerOrModalOpen =
          !!document.querySelector(".ant-drawer-open") || !!document.querySelector(".ant-modal-mask") || document.body.classList.contains("ant-modal-open");
        if (isDrawerOrModalOpen) return;

        isSyncingRef.current = true;
        try {
          const lastUpdatedTimeString = dayjs(useSessionStore.getState().lastUpdatedTime).format("HH:mm:ss");
          const lastUpdatedTime = dayjs(lastUpdatedTimeString, "HH:mm:ss");
          const lastUpdatedOrder = dayjs(orderDelay).format("HH:mm:ss");
          const lastOrderTime = dayjs(lastUpdatedOrder, "HH:mm:ss");

          let cartObj = useCartStore.getState().cart;
          const currentTime = dayjs();
          const syncTime = posConfig?.["Incremental Data Sync Frequency (In Minutes)"] || 15;
          const lastFullSyncTime = await db.dataSyncSummary.where("syncType").equals("Full Sync").reverse().sortBy("syncEndTime");

          const fullSync = posConfig?.["fullDataSyncFrequency"] || 360;
          const diffMinutes = currentTime.diff(lastUpdatedTime, "minutes");
          let lastUpdatedFullTimeString = dayjs(new Date()).format("HH:mm:ss");
          if (lastFullSyncTime?.length > 0) {
            lastUpdatedFullTimeString = dayjs(lastFullSyncTime[0]?.syncEndTime).format("HH:mm:ss");
          }
          const lastUpdatedFullTime = dayjs(lastUpdatedFullTimeString, "HH:mm:ss");
          const diffMinutesFullSync = currentTime.diff(lastUpdatedFullTime, "minutes");
          const diffSeconds = currentTime.diff(lastOrderTime, "seconds");
          if (cartObj.items?.length === 0 && diffMinutesFullSync >= fullSync) {
            useSessionStore.getState().setLastUpdatedTime(null);
            setTillLink(false);
            // This is a background, timer-driven sync — not a user-initiated
            // action — so it must never swap out the visible POS screen for
            // LoadingScreen (setSyncLoad gates that swap in index.js). A
            // non-empty cart already skips this whole branch and never blocks;
            // an empty cart must behave identically, not just "mostly" so.
            let tillData = useAuthStore.getState().tillData;
            const defaultCustomer = selectedSaleTypeData?.customer?.b2cCustomerId ? selectedSaleTypeData?.customer : "";
            setCart({ ...cart, customer: defaultCustomer });
            try {
              await processSync(tillData, "Full Sync");
            } catch (e) {
              console.warn("[AutoSync] Full sync failed:", e?.message);
            }
            resetProgress();
          } else if (cartObj.items?.length === 0 && diffMinutes >= syncTime && diffSeconds >= 15) {
            clearInterval(syncOrdersInterval);
            let tillData = useAuthStore.getState().tillData;
            const defaultCustomer = selectedSaleTypeData?.customer?.b2cCustomerId ? selectedSaleTypeData?.customer : tillData.tillAccess.csBunit.b2cCustomer;
            setCart({ ...cartObj, customer: defaultCustomer });
            try {
              await processSync(tillData, "Incremental Sync");
            } catch (e) {
              console.warn("[AutoSync] Incremental sync failed:", e?.message);
            }
            resetProgress();
          }
        } finally {
          isSyncingRef.current = false;
        }
      }, 1000);
    };

    startSyncInterval();

    return () => {
      clearInterval(syncOrdersInterval);
    };
  }, []);

  // Payment methods filter + keyboard listener
  useEffect(() => {
    let methods = [];
    tillDataPaymentMethods.map((item) => {
      if (item.name === "Cash" && posConfig?.enableCash === "Y") {
        methods.push(item);
      } else if (item.name === "Card" && posConfig?.enableCard === "Y") {
        methods.push(item);
      } else if (item.name !== "Cash" && item.name !== "Card") {
        methods.push(item);
      }
    });
    setPaymentType(methods);
    if (!useTransactionStore.getState().salesReturn) {
      useTransactionStore.getState().setSalesReturn("N");
    }

    window.addEventListener("keydown", handleKey);
    return () => {
      window.removeEventListener("keydown", handleKey);
    };
  }, []);

  // Scroll handler for cart table (debounced to avoid excessive calls)
  useEffect(() => {
    const antTableBody = document.querySelector(".sm-cart-table .ant-table-body");
    if (antTableBody) {
      let scrollTimeout;
      const debouncedFetchMoreData = (event) => {
        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(() => fetchMoreData(event), 200);
      };
      antTableBody.addEventListener("scroll", debouncedFetchMoreData);
      return () => {
        clearTimeout(scrollTimeout);
        antTableBody.removeEventListener("scroll", debouncedFetchMoreData);
      };
    }
  }, [isPurchaseReturn]);

  // Focus return bill search input
  useEffect(() => {
    let t;
    if (displayReturnOrderSearch) {
      t = setTimeout(() => {
        returnBillSearchInputRef.current.focus();
      }, 100);
    }
    return () => clearTimeout(t);
  }, [retrunSearchType, displayReturnOrderSearch]);

  // Cash management data + key value reset
  useEffect(() => {
    filterdParkedList?.map((item, index) => {
      item.key = uuidv4().replace(/-/g, "").toUpperCase();
    });
    (async () => {
      let data = await db.cashInCashOut.toArray();
      setCashManagementData(data);
    })();
    let t;
    if (paymentModal === false) {
      t = setTimeout(() => {
        setKeyValue("0");
      }, 1000);
    }
    return () => clearTimeout(t);
  }, [addCashFlag, paymentModal]);

  // Clear payments when cart items empty
  useEffect(() => {
    if (cart.items.length === 0 && cart.payments.length > 0) {
      cart.payments.map((payment) => {
        handleDeletePayment(payment);
      });
    }
  }, [cart]);

  // Order history type enrichment
  useEffect(() => {
    if (orderHistoryDetails.length > 0) {
      const newArray = orderHistoryDetails.map((item) => {
        const isReturn = item.items.some((product) => product.isReturn);
        // BUG-1764: was `.every(...)`, requiring ALL lines to be the AD deposit
        // product. That only covered a pure deposit-only order. Once the cashier
        // retrieves an Advance Order and adds a real item (Create Advance again),
        // items become [AD line, real line] — `.every` flips false, and the order
        // falls through to the `paid < total` check, which incorrectly reads
        // "Receipt" the moment that combination happens to be fully paid, hiding
        // Retrieve Sale even though the order isn't completed/converted yet. The
        // AD deposit line stays in the order until actual completion (see
        // useCoreEffects.js's `advanceItem` check gating "Complete Order"), so its
        // mere presence — not exclusivity — is the correct "still open" signal.
        const isAdvanceOnlyOrder = item.items?.length > 0 && item.items.some((i) => i.productSegment === "AD");
        const isLayaway = item.layAway === "Y" && (isAdvanceOnlyOrder || Number(item.paid || 0) < Number(item.total || 0));
        const isCancel = (item.total === 0 || item.total === "0.00") && item.totalQty === 0;
        const isCreditSaleRecord = item.creditSale === "Y";
        const type = item.isReturn === "Y" ? "Return" : isLayaway ? "Layaway" : isCancel ? "Cancelled" : isCreditSaleRecord ? "Credit Sale" : "Receipt";
        const newItem = { ...item, type };
        return newItem;
      });
      if (JSON.stringify(newArray) !== JSON.stringify(orderHistoryDetails)) {
        setOrderHistoryDetails(newArray);
      }
    }
  }, [orderHistoryDetails]);

  // Manual discount form prefill
  useEffect(() => {
    if (cart?.manualDiscountData) {
      if (cart?.manualDiscountData?.totalLevelDiscount?.length > 0) {
        (() => {
          const pr = getPricingRules();
          const index = pr.findIndex((md) => md.mPricingrulesId === cart?.items[0]?.mPricingruleId && md.manualDiscount === "Y");
          if (index !== -1) {
            const pricingRule = pr[index];
            const matchedPricingRule = cart?.manualDiscountData?.totalLevelDiscount?.findIndex((item) => item.pricingRule === pricingRule.mPricingrulesId);
            manualDiscountForm.setFieldsValue({
              discountName: pricingRule.mPricingrulesId,
              discountValue: cart?.manualDiscountData?.totalLevelDiscount[matchedPricingRule]?.discountValue,
            });
            setManualDiscountInput(cart?.manualDiscountData?.totalLevelDiscount[matchedPricingRule]?.discountValue);
            setSelectedManualDiscountTypeValue(pricingRule);
          } else {
            manualDiscountForm.resetFields();
            setManualDiscountInput("");
            setSelectedManualDiscountTypeValue("");
          }
        })();
      }
    }
  }, [cart, manualDiscountModalVisible]);

  // WebSocket setup
  const handleWebSocketMessage = useCallback(async (message) => {
    try {
      switch (message.type || message.event) {
        case "SUBSCRIBED":
          console.debug("[v0] [HandleMessage] SUBSCRIBED to bunit:", message.bunitId);
          break;

        case "MISSED_EVENTS_COMPLETE":
          console.debug(`[v0] [HandleMessage] Missed table status events sync complete. Total: ${message.count}`);
          break;

        case "REDIS_RECONNECTED":
          console.debug("[v0] [HandleMessage] Redis reconnected - table status will sync automatically");
          break;

        case "ERROR":
          console.error("[v0] [HandleMessage] WebSocket error message:", message.error || message.message);
          break;

        case "order:new":
          console.debug("[v0] [HandleMessage] New POS order received:", message.data);
          break;

        case "order:update":
          console.debug("[v0] [HandleMessage] POS order updated:", message.data);
          break;

        case "POS_ORDER_UPSERT":
          console.debug("[v0] [HandleMessage] POS_ORDER_UPSERT received, ofdStatus:", message.data?.ofdStatus, "orderId:", message.data?.sOrderID);
          break;

        default:
          console.debug("[v0] [HandleMessage] Unknown message type:", message.type);
      }
    } catch (error) {
      console.error("[v0] [HandleMessage] Error handling WebSocket message:", error);
      console.error("[v0] [HandleMessage] Stack trace:", error.stack);
    }

    console.debug("[v0] [HandleMessage] ========== MESSAGE PROCESSING DONE ==========");
  }, []);

  const { addRawMessageListener } = useWebSocket();

  useEffect(() => {
    const unsubscribe = addRawMessageListener(handleWebSocketMessage);
    return unsubscribe;
  }, [addRawMessageListener, handleWebSocketMessage]);
};

export default useSuperMarketEffects;
